import { normalizarNombre } from "./normalizar";

// ============================================================
// Buscador del Mostrador (Kevin, 16-oct-2026). Lo usan la PWA (que busca en su copia local) y la API.
//   · Tolerancia: el cliente no dice el nombre exacto. Se ignoran tildes y espacios («para cetamol»), se
//     juntan las letras que en castellano suenan igual (b/v, s/c/z, y/ll, h muda, k/qu, j/g) y, desde 5
//     letras, se acepta UNA letra de diferencia («loratadna»).
//   · Tipo de producto: marca, genérico, aseo o galénico. Pinta el fondo y el color del nombre.
//   · Acción por principio activo y aviso de «quedan pocas» por botica.
// ============================================================

export const TIPOS_PRODUCTO = ["marca", "generico", "aseo", "galenico"] as const;
export type TipoProducto = (typeof TIPOS_PRODUCTO)[number];
export const ETIQUETA_TIPO: Record<TipoProducto, string> = { marca: "Marca", generico: "Genérico", aseo: "Aseo", galenico: "Galénico" };

export const esTipoProducto = (x: unknown): x is TipoProducto => typeof x === "string" && (TIPOS_PRODUCTO as readonly string[]).includes(x);

/** Combinación de principios activos («paracetamol + cafeína»): no es el mismo medicamento que sus partes. */
export const esCombinacion = (pa: string | null | undefined): boolean => /[+,/]/.test(pa ?? "");

/**
 * Genérico = se vende con el nombre de su principio activo (la DCI), no con nombre de marca:
 * «PARACETAMOL 500 MG» sí, «PANADOL» no. El catálogo no trae esa marca; la regla sale del nombre.
 */
export function esGenerico(nombre: string, principioActivo: string | null | undefined): boolean {
  const primera = normalizarNombre(principioActivo ?? "").split(/[\s+/,]+/)[0] ?? "";
  return primera.length >= 3 && normalizarNombre(nombre).startsWith(primera);
}

/** El tipo que se pinta: el que marcó la botica o, si no marcó nada, marca/genérico por el nombre. */
export function tipoEfectivo(tipo: string | null | undefined, nombre: string, principioActivo: string | null | undefined): TipoProducto {
  if (esTipoProducto(tipo)) return tipo;
  return esGenerico(nombre, principioActivo) ? "generico" : "marca";
}

// ---- tolerancia ----

/** Sin tildes, en minúsculas y sin nada que no sea letra o número: «Para Cetamol» → «paracetamol». */
export const plano = (s: string | null | undefined): string => normalizarNombre(s ?? "").replace(/[^a-z0-9ñ]/g, "").replace(/ñ/g, "n");

/**
 * Cómo suena en castellano: une las letras que se pronuncian igual. «ivuprofeno» y «ibuprofeno» suenan
 * igual; «amoxisilina» y «amoxicilina» también.
 */
export function sonido(s: string | null | undefined): string {
  // La «x» de «naproxeno» se dice casi como «s» («naproseno»); va antes de usar «x» para la «ch».
  return plano(s)
    .replace(/x/g, "s")
    .replace(/ch/g, "x")
    .replace(/ll/g, "y")
    .replace(/h/g, "")
    .replace(/qu/g, "k")
    .replace(/c([ei])/g, "s$1")
    .replace(/z/g, "s")
    .replace(/c/g, "k")
    .replace(/g([ei])/g, "j$1")
    .replace(/[vw]/g, "b")
    .replace(/y$/, "i")
    .replace(/(.)\1+/g, "$1");
}

/** ¿Hay como mucho UNA letra de diferencia (cambiada, sobrante o faltante)? */
export function difiereEnUnaLetra(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  if (a.length === b.length) return a.slice(i + 1) === b.slice(i + 1);
  return a.length > b.length ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

export type Coincidencia = "exacto" | "sonido" | "letra";
export const LETRAS_MINIMAS_TOLERANCIA = 5;

/**
 * ¿Lo tipeado está en el texto? Primero tal cual (sin tildes ni espacios), después por cómo suena y,
 * desde 5 letras, con una letra de diferencia contra el comienzo de alguna palabra. `null` = no está.
 * `textoPlano` y `textoSonido` se pasan ya calculados cuando se busca en miles de productos.
 */
export function coincidencia(
  texto: string,
  q: string,
  pre?: { textoPlano: string; textoSonido: string; palabrasSonido: string[] },
): Coincidencia | null {
  const qp = plano(q);
  if (!qp) return null;
  const tp = pre?.textoPlano ?? plano(texto);
  if (tp.includes(qp)) return "exacto";
  const qs = sonido(q);
  const ts = pre?.textoSonido ?? sonido(texto);
  if (qs.length >= 3 && ts.includes(qs)) return "sonido";
  if (qs.length < LETRAS_MINIMAS_TOLERANCIA) return null;
  const palabras = pre?.palabrasSonido ?? palabrasSonido(texto);
  for (const p of palabras) {
    for (const largo of [qs.length - 1, qs.length, qs.length + 1]) {
      if (largo > 0 && difiereEnUnaLetra(p.slice(0, largo), qs)) return "letra";
    }
  }
  return null;
}

/** Cada palabra del texto, ya convertida a cómo suena (para la tolerancia de una letra). */
export const palabrasSonido = (texto: string): string[] =>
  normalizarNombre(texto)
    .split(/[^a-z0-9ñ]+/)
    .map(sonido)
    .filter((p) => p.length >= 3);

// ---- «quedan pocas» ----

/**
 * ¿Se vende por unidad suelta (tabletas, cápsulas…)? Sí si tiene blíster o caja (una presentación de más
 * de una unidad) o si su forma lo dice. Lo demás (jarabes, cremas, frascos) se cuenta por envase.
 */
export function sevendePorUnidad(categoria: string | null | undefined, factores: number[]): boolean {
  if (factores.some((f) => f > 1)) return true;
  return /tab|cap|comp|grag|ovul|supos|perla/i.test(categoria ?? "");
}

export type UmbralBotica = { unidad: number; envase: number };
export const UMBRAL_POR_DEFECTO: UmbralBotica = { unidad: 10, envase: 3 };

/**
 * El triángulo amarillo. Un mínimo puesto a mano para ESE producto en Inventario manda; si no hay, el
 * umbral de la botica: menos de 10 tabletas en una botica austera (50 en una grande), menos de 3 jarabes.
 * Sin stock no es «quedan pocas»: se dice aparte.
 */
export function quedanPocas(stock: number | null | undefined, stockMinimo: number | null | undefined, porUnidad: boolean, umbral: UmbralBotica): boolean {
  if (stock == null || stock <= 0) return false;
  if (stockMinimo && stockMinimo > 0) return stock <= stockMinimo;
  return stock < (porUnidad ? umbral.unidad : umbral.envase);
}

export const MAX_UMBRAL = 100_000;
export function errorDeUmbral(n: unknown): string | null {
  if (typeof n !== "number" || !Number.isInteger(n) || n < 0 || n > MAX_UMBRAL) return `el umbral es un número entero entre 0 y ${MAX_UMBRAL}`;
  return null;
}

// ---- acción por principio activo ----

export const MAX_ACCION = 160;
export const MAX_UBICACION = 60;
export type AccionPrincipio = { id: string; principio_activo: string; texto: string };

/**
 * Qué hace este principio activo. La acción se escribe para «ibuprofeno» y vale si el principio activo del
 * producto EMPIEZA con eso («IBUPROFENO 400 MG» también). Una combinación no hereda la acción de sus partes.
 */
export function accionPara(acciones: AccionPrincipio[], principioActivo: string | null | undefined): string | null {
  if (!principioActivo || esCombinacion(principioActivo)) return null;
  const pa = normalizarNombre(principioActivo);
  let mejor: AccionPrincipio | null = null;
  for (const a of acciones) {
    const clave = normalizarNombre(a.principio_activo);
    if (!clave || esCombinacion(clave)) continue;
    if (pa === clave || pa.startsWith(clave + " ")) {
      if (!mejor || clave.length > normalizarNombre(mejor.principio_activo).length) mejor = a;
    }
  }
  return mejor?.texto ?? null;
}

/** Error legible (es-PE) de una acción, o `null` si vale. */
export function errorDeAccion(principioActivo: string, texto: string): string | null {
  const pa = (principioActivo ?? "").trim();
  if (!pa) return "falta el principio activo";
  if (pa.length > 120) return "el principio activo no puede pasar de 120 caracteres";
  if (esCombinacion(pa)) return "escribe un solo principio activo (sin «+», coma ni «/»)";
  const t = (texto ?? "").trim();
  if (!t) return "escribe qué hace el medicamento";
  if (t.length > MAX_ACCION) return `la acción no puede pasar de ${MAX_ACCION} caracteres`;
  return null;
}
